/**
 * reportCore (v3.80.0) — the dependency-free result core shared by the client
 * (School View, Grade Report, Class Report, Grade Team) and the
 * schoolViewSummary callable. THIS FILE IS THE SOURCE OF TRUTH:
 * functions/shared/reportCore.js is GENERATED from it by
 * scripts/syncReportCore.js (a mechanical ESM → CommonJS transform, never a
 * hand copy) and src/__tests__/reportCore.sync.test.js fails `verify` when
 * the copy drifts. Regenerate with `npm run sync:report-core`.
 *
 * Rules for this file: no imports, no process.env, no JSON, no Firestore, no
 * feature flags, no labels — plain functions and string constants only, so
 * the same bytes run under CRA/webpack (src/) and Node CommonJS (functions/).
 *
 * What lives here (moved verbatim from src/utils/schoolView.js and
 * src/utils/bandForScore.js — the identity snapshot + schoolView.test.js pin
 * the behaviour):
 *   - the three synthetic testType keys (hfw / cvc / pa_diagnostic)
 *   - grade keys (NO_GRADE, gradeKeyOf)
 *   - windows and their quarter labels; the Fall / Winter / Spring seasons
 *     (v3.82.0 — the NextPath feed's window mapping, mirrored; parity-tested)
 *   - normalizeResult (one raw doc → { studentId, classId, testType, quarter,
 *     score, max, timestamp, status, doc }); an N/A doc (-1) is NOT a result
 *   - columnKeyOf (PA-D sub-skill columns)
 *   - pickLatestInWindow (higher quarter label, then timestamp)
 *   - aggregateClassHistograms: per (class, column) administered / total and a
 *     de-identified SCORE HISTOGRAM — what the callable returns for peer
 *     classes, banded on the client by bandForScore.
 */

// ─── Test-type keys ──────────────────────────────────────────────────────────

/** Synthetic column keys for the two collections whose docs carry no testType. */
export const HFW_TEST_TYPE = 'hfw';
export const CVC_TEST_TYPE = 'cvc';
export const PAD_TEST_TYPE = 'pa_diagnostic';

// ─── Grades ──────────────────────────────────────────────────────────────────

export const NO_GRADE = '__no_grade__';

export const gradeKeyOf = (cls) => {
  const g = typeof cls?.grade === 'string' ? cls.grade.trim() : '';
  return g ? g : NO_GRADE;
};

// ─── Windows ─────────────────────────────────────────────────────────────────

export const WINDOW_LATEST = 'latest';
export const WINDOWS = ['Q1', 'Q2', 'Q3', 'Q4', WINDOW_LATEST];
export const isWindow = (w) => WINDOWS.includes(w);

// ─── Seasons (v3.82.0) ───────────────────────────────────────────────────────
// The NextPath feed's window vocabulary (functions/src/nextpath/config.ts:
// WINDOW_BY_QUARTER + WINDOW_BY_SCREENER_WINDOW + resolveWindow), mirrored
// here so the Class Report's Fall / Winter / Spring windows file a result in
// the same season the district warehouse — and the sheet fed from it — does.
// TWO COPIES BY DECISION (owner, 2026-09-24): the live feed's code path stays
// untouched and functions/src/nextpath/seasonParity.test.ts pins the two
// tables equal for every input. Deliberately NOT part of WINDOWS: the School
// View / Grade Report / Grade Team selectors and the schoolViewSummary
// callable (isWindow) keep Q1–Q4 | latest only.

export const SEASON_FALL = 'fall';
export const SEASON_WINTER = 'winter';
export const SEASON_SPRING = 'spring';
export const SEASONS = [SEASON_FALL, SEASON_WINTER, SEASON_SPRING];
export const isSeason = (w) => SEASONS.includes(w);

/** Quarter index (the N of "2026QN") → season. Q2 and Q3 are both Winter. */
export const SEASON_OF_QUARTER = { 1: SEASON_FALL, 2: SEASON_WINTER, 3: SEASON_WINTER, 4: SEASON_SPRING };
/** Season → the quarter suffixes it covers. */
export const SEASON_QUARTERS = { [SEASON_FALL]: ['Q1'], [SEASON_WINTER]: ['Q2', 'Q3'], [SEASON_SPRING]: ['Q4'] };
/** The Phonemic Awareness screener's own `window` stamp (its form ids) → season. */
export const SCREENER_WINDOW_SEASON = { september: SEASON_FALL, january: SEASON_WINTER, may: SEASON_SPRING };
/** The five screener step types whose docs carry that stamp (the feed's SCREENER_TYPES). */
export const SCREENER_TEST_TYPES = ['pa_rhyme_recognition', 'pa_rhyme_production', 'pa_blending_syllables', 'pa_segmenting_syllables', 'pa_onset_fluency'];

const QUARTER_LABEL_RE = /^\d{4}Q([1-4])$/;

/** "2026Q2" → 'winter'. Missing / unparseable → ''. */
export function seasonOfQuarter(quarter) {
  const m = typeof quarter === 'string' ? QUARTER_LABEL_RE.exec(quarter) : null;
  return m ? SEASON_OF_QUARTER[Number(m[1])] : '';
}

/**
 * A doc's season — the feed's resolveWindow rule: the five screener types use
 * the doc's own `window` when it is a known form id (a May form given in late
 * April is a Spring administration); every other type, and a screener doc
 * with no or an unknown stamp, derives from the `quarter` label.
 */
export function resolveSeason(testType, doc) {
  if (SCREENER_TEST_TYPES.includes(testType) && doc && typeof doc.window === 'string') {
    const fromStamp = SCREENER_WINDOW_SEASON[doc.window];
    if (fromStamp) return fromStamp;
  }
  return seasonOfQuarter(doc ? doc.quarter : undefined);
}

const yearLabels = (startYear) => ['Q1', 'Q2', 'Q3', 'Q4'].map((q) => `${startYear}${q}`);

/** Quarter labels a window COVERS for one school year (a season: its quarters). */
export function quarterLabelsForWindow(startYear, window) {
  if (window === WINDOW_LATEST) return yearLabels(startYear);
  if (isSeason(window)) return SEASON_QUARTERS[window].map((q) => `${startYear}${q}`);
  return [`${startYear}${window}`];
}

/**
 * Quarter labels a window's results are FETCHED with (v3.82.0): one for a
 * quarter window; all four for Latest AND for a season — a screener doc's own
 * window stamp can place it in a season other than its quarter's, so a season
 * has to see the whole year to honour it. Seasons and Latest therefore share
 * one fetch; the loaders cache by this label set, not by window.
 */
export function fetchLabelsForWindow(startYear, window) {
  if (window === WINDOW_LATEST || isSeason(window)) return yearLabels(startYear);
  return [`${startYear}${window}`];
}

/**
 * The normalized results that belong to a window (v3.82.0). Quarter windows
 * and Latest: the doc's quarter is one of the window's labels. A season: the
 * doc is in the school year and resolveSeason (doc.window first for the five
 * screener types, quarter otherwise) names this season — so a 'may' form
 * stamped Q3 is out of Winter and in Spring, exactly as the feed files it.
 */
export function resultsInWindow(results, startYear, window) {
  if (!isSeason(window)) {
    const allowed = new Set(quarterLabelsForWindow(startYear, window));
    return results.filter((r) => r && allowed.has(r.quarter));
  }
  const year = new Set(yearLabels(startYear));
  return results.filter((r) => r && year.has(r.quarter) && resolveSeason(r.testType, r.doc || r) === window);
}

// ─── Results ─────────────────────────────────────────────────────────────────

export const toDate = (ts) => {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
};
export const scoreOf = (d) => (d.correctCount !== undefined && d.correctCount !== null
  ? Number(d.correctCount)
  : Object.values(d.responses || {}).filter((v) => v === 'Correct').length);
export const maxOf = (d) => d.totalPossible ?? d.totalLetters ?? d.totalWords ?? (d.responses ? Object.keys(d.responses).length : null);

/**
 * Normalize one raw doc from one of the three collections. `collection` is
 * 'assessments' | 'hfw_assessments' | 'cvc_assessments' (HFW / CVC docs carry
 * no testType — the collection names the column).
 */
export function normalizeResult(doc, collection = 'assessments') {
  const testType = collection === 'hfw_assessments' ? HFW_TEST_TYPE
    : collection === 'cvc_assessments' ? CVC_TEST_TYPE
      : doc.testType;
  if (!testType || !doc.studentId) return null;
  return {
    id: doc.id,
    studentId: doc.studentId,
    classId: doc.classId,
    testType,
    quarter: doc.quarter,
    score: scoreOf(doc),
    max: maxOf(doc),
    timestamp: toDate(doc.timestamp),
    status: doc.status,
    doc,
  };
}

/** Column key for a normalized result: the testType, or `pa_diagnostic:<subskill>`. */
export const columnKeyOf = (r) => (r.testType === PAD_TEST_TYPE ? `${PAD_TEST_TYPE}:${r.doc?.padSubskillId || 'unknown'}` : r.testType);

export const QUARTER_WEIGHT = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
export const qWeight = (label) => QUARTER_WEIGHT[String(label || '').slice(4)] || 0;
export const isNA = (r) => r.score === -1 || r.score === null || Number.isNaN(r.score);

/**
 * Latest attempt per (studentId, columnKey) inside the window's labels.
 * N/A docs (correctCount -1) are dropped first — they are not results.
 * Returns Map<`${studentId}|${columnKey}`, { ...result, attemptCount }>.
 */
export function pickLatestInWindow(results, labels) {
  const allowed = new Set(labels);
  const latest = new Map();
  for (const r of results) {
    if (!r || !allowed.has(r.quarter) || isNA(r)) continue;
    const key = `${r.studentId}|${columnKeyOf(r)}`;
    const cur = latest.get(key);
    if (!cur) { latest.set(key, { ...r, attemptCount: 1 }); continue; }
    const newer = qWeight(r.quarter) > qWeight(cur.quarter)
      || (qWeight(r.quarter) === qWeight(cur.quarter) && (r.timestamp?.getTime() || 0) > (cur.timestamp?.getTime() || 0));
    latest.set(key, newer ? { ...r, attemptCount: cur.attemptCount + 1 } : { ...cur, attemptCount: cur.attemptCount + 1 });
  }
  return latest;
}

// ─── De-identified aggregation (the callable's shape) ────────────────────────

/**
 * The non-identifying part of a result that the client needs to label and
 * band a column: the testType and, for PA-D, the tier + sub-skill ids (the
 * band cuts are tier data). Never a student field.
 */
export function columnMetaOf(r) {
  const meta = { testType: r.testType };
  if (r.testType === PAD_TEST_TYPE) {
    meta.padTierId = r.doc?.padTierId ?? null;
    meta.padSubskillId = r.doc?.padSubskillId ?? null;
  }
  return meta;
}

/** A PA-D doc saved mid-administration has no band (the write path's rule). */
export const isPartialResult = (r) => r.testType === PAD_TEST_TYPE && r.doc?.padStatus === 'partial';
/** A PA-D doc's STORED band id (a level, never a student field) — bandForScore's fallback when the tier data cannot band it. */
export const storedBandOf = (r) => (r.testType === PAD_TEST_TYPE && typeof r.doc?.band === 'string' && r.doc.band ? r.doc.band : null);

/**
 * Per class, per column: administered / total and a score histogram —
 * [{ score, max, n, partial?, band? }] (band = a PA-D doc's stored level) — with the first-seen (roster order) meta,
 * legendMax (the first doc's max) and max (the first truthy max), exactly the
 * inputs deriveSchoolSummary's summarizeColumns reads per student. Nothing per student survives: no ids, names, docs or timestamps.
 *
 * @param {{ classes: Array<{id}>, students: Array<{id, classId}>, latest: Map }} input
 *   `latest` = pickLatestInWindow over the scope's normalized results.
 * @returns {{ classes: Array<{ classId, studentCount, columns }>, columnKeys: Array<{ key, meta }> }}
 */
export function aggregateClassHistograms({ classes, students, latest }) {
  const byClass = new Map(classes.map((c) => [c.id, []]));
  students.forEach((s) => { if (byClass.has(s.classId)) byClass.get(s.classId).push(s.id); });

  // Column keys present anywhere in the scope, first-seen order, with a meta sample.
  const keyMeta = new Map();
  for (const [k, r] of latest) {
    const sid = k.slice(0, k.indexOf('|'));
    const col = columnKeyOf(r);
    if (!keyMeta.has(col) && students.some((s) => s.id === sid && byClass.has(s.classId))) keyMeta.set(col, columnMetaOf(r));
  }

  const out = [];
  for (const c of classes) {
    const ids = byClass.get(c.id) || [];
    const columns = {};
    for (const col of keyMeta.keys()) {
      let administered = 0;
      let meta = null;
      let max = null;        // first truthy max (summarizeColumns' maxSeen)
      let legendMax = null;  // the FIRST doc's max, as-is (what its legend is built from)
      const hist = new Map();
      for (const sid of ids) {
        const r = latest.get(`${sid}|${col}`);
        if (!r) continue;
        administered += 1;
        if (!meta) { meta = columnMetaOf(r); legendMax = r.max ?? null; }
        if (max === null && r.max) max = r.max;
        const partial = isPartialResult(r);
        const stored = storedBandOf(r);
        const hk = `${r.score}|${r.max ?? ''}|${partial ? 'p' : ''}|${stored || ''}`;
        const cur = hist.get(hk);
        if (cur) cur.n += 1;
        else hist.set(hk, { score: r.score, max: r.max ?? null, n: 1, ...(partial ? { partial: true } : {}), ...(stored ? { band: stored } : {}) });
      }
      if (administered === 0) continue; // a class block carries only its present columns
      columns[col] = { administered, total: ids.length, max, legendMax, hist: [...hist.values()], meta };
    }
    out.push({ classId: c.id, studentCount: ids.length, columns });
  }
  return { classes: out, columnKeys: [...keyMeta.entries()].map(([key, meta]) => ({ key, meta })) };
}
